import { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { format, parseISO } from "date-fns";
import { es } from "date-fns/locale";
import { Trash2, Clock, Pencil, X, Tag, MessageCircle } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "./ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { ShareButton } from "./ShareMood";
import { useAuth } from "../contexts/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MOOD_EMOJIS = {
  very_happy: "😄",
  happy: "🙂",
  neutral: "😐",
  sad: "😔",
  very_sad: "😢",
  angry: "😡",
  anxious: "😰",
  tired: "😴",
  in_love: "😍",
  sick: "🤒",
  surprised: "😲",
  relaxed: "😌",
  sporty: "💪",
  forgiven: "🙏",
};

const MOOD_COLORS = {
  very_happy: "border-green-500/50 bg-green-500/10",
  happy: "border-lime-500/50 bg-lime-500/10",
  neutral: "border-yellow-500/50 bg-yellow-500/10",
  sad: "border-blue-500/50 bg-blue-500/10",
  very_sad: "border-indigo-500/50 bg-indigo-500/10",
  angry: "border-red-500/50 bg-red-500/10",
  anxious: "border-orange-500/50 bg-orange-500/10",
  tired: "border-slate-500/50 bg-slate-500/10",
  in_love: "border-pink-500/50 bg-pink-500/10",
  sick: "border-emerald-500/50 bg-emerald-500/10",
  surprised: "border-amber-500/50 bg-amber-500/10",
  relaxed: "border-cyan-500/50 bg-cyan-500/10",
  sporty: "border-orange-500/50 bg-orange-500/10",
  forgiven: "border-violet-500/50 bg-violet-500/10",
};

const MOOD_LABELS = {
  very_happy: "Muy Feliz",
  happy: "Bien",
  neutral: "Neutral",
  sad: "Triste",
  very_sad: "Muy Triste",
  angry: "Enojado",
  anxious: "Ansioso",
  tired: "Cansado",
  in_love: "Enamorado",
  sick: "Enfermo",
  surprised: "Sorprendido",
  relaxed: "Relajado",
  sporty: "Deportista",
  forgiven: "Perdonado",
};

// Format time from ISO timestamp
const formatTime = (timestamp) => {
  try {
    const date = parseISO(timestamp);
    return format(date, "HH:mm", { locale: es });
  } catch {
    return "";
  }
};

// Group moods by date
const groupMoodsByDate = (moods) => {
  const grouped = {};
  moods.forEach((mood) => {
    const date = mood.date;
    if (!grouped[date]) {
      grouped[date] = [];
    }
    grouped[date].push(mood);
  });
  
  // Sort entries within each day by timestamp (most recent first)
  Object.keys(grouped).forEach((date) => {
    grouped[date].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
  });
  
  return grouped;
};

export const RecentMoods = ({ moods, loading, onUpdate }) => {
  const [deletingId, setDeletingId] = useState(null);
  const [editingMood, setEditingMood] = useState(null);
  const [sharingToChat, setSharingToChat] = useState(null);
  const { authMode } = useAuth();
  const navigate = useNavigate();

  // Group moods by date
  const groupedMoods = useMemo(() => groupMoodsByDate(moods), [moods]);
  const sortedDates = useMemo(() => 
    Object.keys(groupedMoods).sort((a, b) => new Date(b) - new Date(a)),
    [groupedMoods]
  );

  const handleDelete = async (id) => {
    setDeletingId(id);
    try {
      await axios.delete(`${API}/moods/${id}`);
      toast.success("Registro eliminado");
      onUpdate?.();
    } catch (error) {
      console.error("Error deleting mood:", error);
      toast.error("Error al eliminar");
    } finally {
      setDeletingId(null);
    }
  };

  const handleEditSave = () => {
    setEditingMood(null);
    onUpdate?.();
    toast.success("Registro actualizado");
  };

  if (loading) {
    return (
      <div className="flex gap-4 overflow-x-auto pb-2 mood-scroll">
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="skeleton min-w-[200px] h-24 rounded-xl" />
        ))}
      </div>
    );
  }

  if (moods.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-slate-500 font-inter">
          No hay registros aún. ¡Comienza registrando tu estado de ánimo!
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="recent-moods-list">
      {sortedDates.map((date, dateIndex) => (
        <motion.div
          key={date}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: dateIndex * 0.1 }}
          className="space-y-2"
        >
          {/* Date Header */}
          <div className="flex items-center gap-2 mb-2">
            <div className="h-px flex-1 bg-gradient-to-r from-neon-cyan/30 to-transparent" />
            <span className="font-orbitron text-xs text-neon-cyan px-2">
              {format(parseISO(date), "EEEE, d MMMM", { locale: es })}
            </span>
            <div className="h-px flex-1 bg-gradient-to-l from-neon-purple/30 to-transparent" />
          </div>
          
          {/* Moods for this date */}
          <div className="flex gap-3 overflow-x-auto pb-2 mood-scroll">
            <AnimatePresence>
              {groupedMoods[date].map((mood, index) => (
                <motion.div
                  key={mood.id}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ delay: index * 0.05 }}
                  className={`min-w-[180px] p-3 rounded-xl border ${MOOD_COLORS[mood.mood]} relative group`}
                  data-testid={`mood-entry-${mood.id}`}
                >
                  {/* Action Buttons */}
                  <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    {/* Edit Button */}
                    <button
                      onClick={() => setEditingMood(mood)}
                      className="p-1.5 rounded-lg bg-neon-cyan/10 border border-neon-cyan/30 text-neon-cyan hover:bg-neon-cyan/20"
                      data-testid={`edit-mood-${mood.id}`}
                      title="Editar"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    {/* Share to Chat Button - only for registered users */}
                    {authMode !== "incognito" && (
                      <button
                        onClick={() => setSharingToChat(mood)}
                        className="p-1.5 rounded-lg bg-neon-purple/10 border border-neon-purple/30 text-neon-purple hover:bg-neon-purple/20"
                        data-testid={`share-chat-${mood.id}`}
                        title="Compartir en chat"
                      >
                        <MessageCircle className="w-3.5 h-3.5" />
                      </button>
                    )}
                    <ShareButton mood={mood.mood} note={mood.note} date={mood.date} />
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <button
                          className="p-1.5 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 hover:bg-red-500/20"
                          data-testid={`delete-mood-${mood.id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </AlertDialogTrigger>
                      <AlertDialogContent className="bg-void-paper border border-white/10">
                        <AlertDialogHeader>
                          <AlertDialogTitle className="font-orbitron text-white">
                            ¿Eliminar registro?
                          </AlertDialogTitle>
                          <AlertDialogDescription className="text-slate-400">
                            Esta acción no se puede deshacer. Se eliminará permanentemente este registro de estado de ánimo.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel className="bg-transparent border-white/10 text-slate-300 hover:bg-white/5 hover:text-white">
                            Cancelar
                          </AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => handleDelete(mood.id)}
                            className="bg-red-500/20 border border-red-500/50 text-red-400 hover:bg-red-500/30"
                            data-testid={`confirm-delete-${mood.id}`}
                          >
                            {deletingId === mood.id ? "Eliminando..." : "Eliminar"}
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>

                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{MOOD_EMOJIS[mood.mood]}</span>
                    <div className="flex-1 min-w-0">
                      <p className="font-rajdhani font-semibold text-white text-sm">
                        {MOOD_LABELS[mood.mood]}
                      </p>
                      {/* Time indicator */}
                      <div className="flex items-center gap-1 text-xs text-slate-500">
                        <Clock className="w-3 h-3" />
                        <span>{formatTime(mood.timestamp)}</span>
                      </div>
                    </div>
                  </div>
                  
                  {mood.note && (
                    <p className="text-xs text-slate-400 font-inter line-clamp-2 mt-2 border-t border-white/5 pt-2">
                      "{mood.note}"
                    </p>
                  )}
                  
                  {/* Tags display */}
                  {mood.tags && mood.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {mood.tags.slice(0, 3).map((tag, i) => (
                        <span key={i} className="text-[10px] px-1.5 py-0.5 rounded-full bg-neon-purple/20 text-neon-purple border border-neon-purple/30 flex items-center gap-0.5">
                          <Tag className="w-2 h-2" />
                          {tag}
                        </span>
                      ))}
                      {mood.tags.length > 3 && (
                        <span className="text-[10px] text-slate-500">+{mood.tags.length - 3}</span>
                      )}
                    </div>
                  )}
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        </motion.div>
      ))}

      {/* Edit Modal */}
      <Dialog open={!!editingMood} onOpenChange={(open) => !open && setEditingMood(null)}>
        <DialogContent className="bg-void-paper border border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-orbitron text-white flex items-center gap-2">
              <Pencil className="w-5 h-5 text-neon-cyan" />
              Editar Registro
            </DialogTitle>
          </DialogHeader>
          {editingMood && (
            <EditMoodForm 
              mood={editingMood} 
              onSave={handleEditSave}
              onCancel={() => setEditingMood(null)}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Share to Chat Modal */}
      <Dialog open={!!sharingToChat} onOpenChange={(open) => !open && setSharingToChat(null)}>
        <DialogContent className="bg-void-paper border border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="font-orbitron text-white flex items-center gap-2">
              <MessageCircle className="w-5 h-5 text-neon-purple" />
              Compartir en Chat
            </DialogTitle>
          </DialogHeader>
          {sharingToChat && (
            <ShareToChatForm 
              mood={sharingToChat} 
              onSuccess={() => {
                setSharingToChat(null);
                toast.success("¡Mood compartido en el chat!");
              }}
              onCancel={() => setSharingToChat(null)}
              navigate={navigate}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Edit Mood Form Component
const EditMoodForm = ({ mood, onSave, onCancel }) => {
  const [selectedMood, setSelectedMood] = useState(mood.mood);
  const [note, setNote] = useState(mood.note || "");
  const [tags, setTags] = useState(mood.tags || []);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/moods/${mood.id}`, {
        mood: selectedMood,
        note: note.trim() || null,
        tags: tags,
      });
      onSave();
    } catch (error) {
      console.error("Error updating mood:", error);
      toast.error("Error al actualizar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="text-sm text-slate-400 mb-2">
        Fecha: {format(parseISO(mood.date), "EEEE, d MMMM yyyy", { locale: es })}
      </div>
      
      {/* Mood Selection Grid */}
      <div className="grid grid-cols-4 sm:grid-cols-7 gap-2">
        {Object.entries(MOOD_EMOJIS).map(([moodId, emoji]) => (
          <motion.button
            key={moodId}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setSelectedMood(moodId)}
            className={`flex flex-col items-center gap-1 p-2 rounded-lg transition-all ${
              selectedMood === moodId
                ? "bg-neon-cyan/20 border border-neon-cyan/50"
                : "bg-black/30 border border-white/10 hover:border-white/20"
            }`}
          >
            <span className="text-xl">{emoji}</span>
            <span className="text-[10px] text-slate-400">{MOOD_LABELS[moodId]}</span>
          </motion.button>
        ))}
      </div>

      {/* Note */}
      <div>
        <label className="text-sm text-slate-400 mb-1 block">Nota (opcional)</label>
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="¿Cómo te sientes?"
          className="w-full p-3 rounded-lg bg-black/30 border border-white/10 text-white placeholder:text-slate-600 focus:border-neon-cyan/50 focus:outline-none resize-none"
          rows={3}
        />
      </div>

      {/* Tags */}
      <div>
        <label className="text-sm text-slate-400 mb-2 block flex items-center gap-1">
          <Tag className="w-3 h-3" /> Etiquetas
        </label>
        <div className="flex flex-wrap gap-2">
          {["work", "family", "health", "friends", "love", "money", "study", "exercise", "sleep", "food"].map((tagId) => {
            const tagLabels = {
              work: "💼 Trabajo",
              family: "👨‍👩‍👧‍👦 Familia",
              health: "🏥 Salud",
              friends: "👥 Amigos",
              love: "❤️ Amor",
              money: "💰 Dinero",
              study: "📚 Estudio",
              exercise: "🏃 Ejercicio",
              sleep: "😴 Sueño",
              food: "🍽️ Comida",
            };
            const isSelected = tags.includes(tagId);
            return (
              <button
                key={tagId}
                onClick={() => {
                  if (isSelected) {
                    setTags(tags.filter(t => t !== tagId));
                  } else {
                    setTags([...tags, tagId]);
                  }
                }}
                className={`px-2 py-1 rounded-full text-xs transition-all ${
                  isSelected
                    ? "bg-neon-cyan/20 border border-neon-cyan/50 text-neon-cyan"
                    : "bg-white/5 border border-white/10 text-slate-400 hover:border-white/20"
                }`}
              >
                {tagLabels[tagId]}
              </button>
            );
          })}
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-2 pt-2">
        <button
          onClick={onCancel}
          className="flex-1 py-2 px-4 rounded-lg bg-white/5 border border-white/10 text-slate-400 hover:bg-white/10"
        >
          Cancelar
        </button>
        <button
          onClick={handleSave}
          disabled={saving || !selectedMood}
          className="flex-1 py-2 px-4 rounded-lg bg-neon-cyan/20 border border-neon-cyan/50 text-neon-cyan hover:bg-neon-cyan/30 disabled:opacity-50"
        >
          {saving ? "Guardando..." : "Guardar Cambios"}
        </button>
      </div>
    </div>
  );
};

// Share to Chat Component
const ShareToChatForm = ({ mood, onSuccess, onCancel, navigate }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [searching, setSearching] = useState(false);
  const [sending, setSending] = useState(false);

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    
    setSearching(true);
    try {
      const response = await axios.get(`${API}/users/search`, {
        params: { q: searchQuery.trim() }
      });
      setSearchResults(response.data);
    } catch (error) {
      console.error("Search error:", error);
      toast.error("Error en la búsqueda");
    } finally {
      setSearching(false);
    }
  };

  const handleSend = async () => {
    if (!selectedUser) return;
    
    setSending(true);
    try {
      // Create a mood share message
      const moodMessage = `🎭 *Compartiendo mi estado de ánimo*\n\n${MOOD_EMOJIS[mood.mood]} ${MOOD_LABELS[mood.mood]}\n${mood.note ? `\n"${mood.note}"` : ""}\n\n📅 ${format(parseISO(mood.date), "d MMMM yyyy", { locale: es })}`;
      
      // Send message to chat
      await axios.post(`${API}/chat/send`, {
        to_user_id: selectedUser.user_id,
        message: moodMessage
      });
      
      onSuccess();
      // Navigate to chat with this user
      navigate(`/chat?user=${selectedUser.user_id}`);
    } catch (error) {
      console.error("Send error:", error);
      toast.error("Error al enviar");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Preview of mood being shared */}
      <div className={`p-3 rounded-xl border ${MOOD_COLORS[mood.mood]}`}>
        <div className="flex items-center gap-3">
          <span className="text-3xl">{MOOD_EMOJIS[mood.mood]}</span>
          <div>
            <p className="font-rajdhani font-semibold text-white">
              {MOOD_LABELS[mood.mood]}
            </p>
            <p className="text-xs text-slate-400">
              {format(parseISO(mood.date), "d MMMM yyyy", { locale: es })}
            </p>
          </div>
        </div>
        {mood.note && (
          <p className="text-sm text-slate-300 mt-2 italic">"{mood.note}"</p>
        )}
      </div>

      {/* User Search */}
      <div>
        <label className="text-sm text-slate-400 mb-2 block">
          Buscar usuario por ID o email
        </label>
        <div className="flex gap-2">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            placeholder="@usuario o email@ejemplo.com"
            className="flex-1 px-3 py-2 rounded-lg bg-black/30 border border-white/10 text-white placeholder:text-slate-600 focus:border-neon-purple/50 focus:outline-none"
          />
          <button
            onClick={handleSearch}
            disabled={searching || !searchQuery.trim()}
            className="px-4 py-2 rounded-lg bg-neon-purple/20 border border-neon-purple/50 text-neon-purple hover:bg-neon-purple/30 disabled:opacity-50"
          >
            {searching ? "..." : "Buscar"}
          </button>
        </div>
      </div>

      {/* Search Results */}
      {searchResults.length > 0 && (
        <div className="space-y-2 max-h-40 overflow-y-auto">
          {searchResults.map((user) => (
            <button
              key={user.user_id}
              onClick={() => setSelectedUser(user)}
              className={`w-full p-3 rounded-lg text-left transition-all ${
                selectedUser?.user_id === user.user_id
                  ? "bg-neon-purple/20 border border-neon-purple/50"
                  : "bg-white/5 border border-white/10 hover:bg-white/10"
              }`}
            >
              <p className="font-rajdhani text-white">@{user.user_id}</p>
              {user.email && (
                <p className="text-xs text-slate-400">{user.email}</p>
              )}
            </button>
          ))}
        </div>
      )}

      {searchResults.length === 0 && searchQuery && !searching && (
        <p className="text-sm text-slate-500 text-center py-2">
          No se encontraron usuarios
        </p>
      )}

      {/* Actions */}
      <div className="flex gap-2 pt-2">
        <button
          onClick={onCancel}
          className="flex-1 py-2 px-4 rounded-lg bg-white/5 border border-white/10 text-slate-400 hover:bg-white/10"
        >
          Cancelar
        </button>
        <button
          onClick={handleSend}
          disabled={sending || !selectedUser}
          className="flex-1 py-2 px-4 rounded-lg bg-neon-purple/20 border border-neon-purple/50 text-neon-purple hover:bg-neon-purple/30 disabled:opacity-50"
        >
          {sending ? "Enviando..." : "Enviar"}
        </button>
      </div>
    </div>
  );
};
